import { useState, useCallback } from 'react'
import { Box, Button, IconButton, InputAdornment, Link } from '@mui/material'
import { useForm } from 'react-hook-form'
import { FormContainer, TextFieldElement } from 'react-hook-form-mui'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { signinManual } from 'src/utils/auth'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { border: 'none', borderBottom: '1px solid #e2e8f0' },
    '&:hover fieldset': { borderBottom: '1px solid #cbd5e1' },
    '&.Mui-focused fieldset': { border: 'none', borderBottom: '2px solid #4277b9' }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    color: '#94a3b8',
    '&.Mui-focused': { color: '#4277b9' }
  }
}

type Props = { setError: (msg: string) => void }

type LoginLocationState = { from?: { pathname: string } }

export function LoginForm({ setError }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formContext = useForm<{ mail: string; password: string }>({
    defaultValues: { mail: '', password: '' }
  })

  const onSubmit = useCallback(
    async (data: { mail: string; password: string }) => {
      setIsSubmitting(true)
      try {
        await signinManual(data.mail, data.password)
        const from = (location.state as LoginLocationState | null)?.from?.pathname
        navigate(from && from !== '/login' ? from : '/dashboard', { replace: true })
      } catch (err: any) {
        const code = err?.code ?? ''
        const devHint = import.meta.env.DEV && code ? ` [${code}]` : ''
        if (code === 'auth/too-many-requests') setError('Trop de tentatives. Réessayez plus tard.' + devHint)
        else if (code === 'auth/invalid-email') setError('Email invalide.' + devHint)
        else if (code === 'auth/wrong-password' || code === 'auth/user-not-found')
          setError('Email ou mot de passe incorrect.' + devHint)
        else setError((err?.message ?? 'Erreur de connexion.') + devHint)
      } finally {
        setIsSubmitting(false)
      }
    },
    [location.state, navigate, setError]
  )

  return (
    <FormContainer formContext={formContext} onSuccess={onSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextFieldElement name="mail" fullWidth type="email" placeholder="Adresse email" sx={fieldSx} variant="outlined" />
        <TextFieldElement
          name="password"
          fullWidth
          autoComplete="current-password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Mot de passe"
          sx={fieldSx}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((p) => !p)}
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ textAlign: 'right', mt: 0.5, mb: 2 }}>
          <Link
            component={RouterLink}
            to="/forgot"
            sx={{ color: '#64748b', textDecoration: 'none', fontSize: '0.8rem', '&:hover': { color: '#4277b9' } }}>
            Mot de passe oublié ?
          </Link>
        </Box>

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          disableElevation
          sx={{
            py: 1.5,
            borderRadius: '24px',
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'none',
            backgroundColor: '#4277b9',
            '&:hover': { backgroundColor: '#3666a3' }
          }}>
          {isSubmitting ? 'Connexion…' : 'Se connecter'}
        </Button>
      </Box>
    </FormContainer>
  )
}
